import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Call, CallStatus } from '@src/types/calls';

// How many ended call ids are remembered, so a late or replayed event for one of them
// cannot bring it back on screen (and with it, an Answer button for a finished call).
const ENDED_CALL_IDS_LIMIT = 50;

// How far along a call is. receivedCall never moves a call backwards: a replayed or
// out-of-order WAITING_* must not put the Answer button back on a live or finished call.
const STATUS_RANK: Partial<Record<CallStatus, number>> = {
  [CallStatus.WAITING_AGENT]: 0,
  [CallStatus.WAITING_ANSWER]: 0,
  [CallStatus.ONGOING]: 1,
  [CallStatus.MISSED]: 2,
  [CallStatus.REJECTED]: 2,
  [CallStatus.TERMINATED]: 2,
  [CallStatus.FAILED]: 2,
};
const rank = (status: CallStatus) => STATUS_RANK[status] ?? 0;

interface CallsInitialState {
  activeCallId: string | undefined;
  // The incoming call this agent is answering right now, shared by every Answer button
  // (chat list and agent console mount their own hook). Set when an answer starts,
  // cleared when it fails or when the call reaches ONGOING or a terminal status.
  answeringCallId?: string;
  endedCallIds?: string[];
  calls: Call[];
}

const initialState: CallsInitialState = {
  activeCallId: '',
  endedCallIds: [],
  calls: [],
};

// An ended call id is final: nothing that arrives later for it may act on it.
const isEnded = (state: CallsInitialState, callId: Call['id']) =>
  !!state.endedCallIds?.includes(callId);

// Take a call off this agent's screen and release whatever it held.
const dropCall = (state: CallsInitialState, callId: Call['id']) => {
  const index = state.calls.findIndex(({ id }) => id === callId);
  if (index !== -1) state.calls.splice(index, 1);
  if (callId === state.activeCallId) delete state.activeCallId;
  if (callId === state.answeringCallId) delete state.answeringCallId;
};

const callsSlice = createSlice({
  name: 'calling',
  initialState,
  reducers: {
    receivedCall: (state, { payload: call }: PayloadAction<Call>) => {
      // An ended call never comes back: a chat's last message still carries it as
      // WAITING_AGENT, and the list's sync replays that on every remount.
      if (isEnded(state, call.id)) return;
      const current = state.calls.find(({ id }) => id === call.id);
      if (current && rank(call.status) < rank(current.status)) return;

      const indexByChatId = state.calls.findIndex(
        ({ chatId }) => chatId === call.chatId
      );
      if (indexByChatId !== -1) state.calls.splice(indexByChatId, 1);

      const index = state.calls.findIndex(({ id }) => id === call.id);
      if (index === -1) state.calls.push(call);
    },
    startedCall: (
      state,
      {
        payload: { callId },
      }: PayloadAction<{ callId: Call['id']; startedAt: Call['startedAt'] }>
    ) => {
      // An ended call never becomes active again: an ONGOING for it can still arrive
      // (late, or redelivered after the call ended here).
      if (isEnded(state, callId)) return;
      const index = state.calls.findIndex(({ id }) => id === callId);
      if (index !== -1) {
        state.calls[index].status = CallStatus.ONGOING;
        state.calls[index].startedAt = Date.now();
      }
      state.activeCallId = callId;
      if (callId === state.answeringCallId) delete state.answeringCallId;
    },

    answeringStarted: (
      state,
      { payload: callId }: PayloadAction<Call['id']>
    ) => {
      state.answeringCallId = callId;
    },

    answeringFinished: (
      state,
      { payload: callId }: PayloadAction<Call['id']>
    ) => {
      if (callId === state.answeringCallId) delete state.answeringCallId;
    },

    missedCall: (state, { payload: callId }: PayloadAction<Call['id']>) => {
      const index = state.calls.findIndex(({ id }) => id === callId);
      if (index !== -1) state.calls[index].status = CallStatus.MISSED;

      if (callId === state.activeCallId) delete state.activeCallId;
      if (callId === state.answeringCallId) delete state.answeringCallId;
    },

    rejectedCall: (state, { payload: callId }: PayloadAction<Call['id']>) => {
      const index = state.calls.findIndex(({ id }) => id === callId);
      if (index !== -1) state.calls[index].status = CallStatus.REJECTED;
      if (callId === state.answeringCallId) delete state.answeringCallId;
    },

    // The call left this agent's view (the chat was taken by another agent, or moved to
    // a department that is not theirs) but may still be ringing: forget it here without
    // remembering it as ended, so it can come back if the chat does.
    dismissedCall: (state, { payload: callId }: PayloadAction<Call['id']>) => {
      dropCall(state, callId);
    },

    endedCall: (state, { payload: callId }: PayloadAction<Call['id']>) => {
      dropCall(state, callId);
      // A preloaded state may predate this field.
      state.endedCallIds ??= [];
      if (!state.endedCallIds.includes(callId)) {
        state.endedCallIds.push(callId);
        if (state.endedCallIds.length > ENDED_CALL_IDS_LIMIT)
          state.endedCallIds.shift();
      }
    },
  },
});

export const {
  receivedCall,
  startedCall,
  answeringStarted,
  answeringFinished,
  missedCall,
  rejectedCall,
  dismissedCall,
  endedCall,
} = callsSlice.actions;

export default callsSlice.reducer;
